import { Component, OnDestroy, ViewEncapsulation } from "@angular/core";
import { NgForm } from "@angular/forms";
import { DateFilterModel } from "ag-grid-community";
import { addYears } from "date-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { PayrollService } from "../services/payroll.service";
import { PpsUtilityService } from "../services/pps-utility.service";
import { IDateRange } from "../shared/models/date-range";
import { EmployeeBase } from "../shared/models/employee-base";
import { IEntity } from "../shared/models/entity";
import { IJobCode } from "../shared/models/job-code";
import { IRosterCode } from "../shared/models/roster-code";
import { TimesheetGenerationModel } from "../shared/models/timesheet-generation-model";
import { TimesheetService } from "../services/timesheet.service";
import { SubSink } from "subsink";
import { RosterCodes } from "../shared/models/roster-codes";
import { RosterCodeService } from "../services/roster-code.service";
import { ConfirmationService } from "primeng/api";
import { ToastService } from "../shared/toast/toast.service";

@Component({
  selector: "pps-generate-timesheet-form",
  templateUrl: "./generate-timesheet-form.component.html",
  styles:[`
  .p-dialog.p-confirm-dialog .p-confirm-dialog-message{
    max-height:400px;
  }
  `],
  encapsulation: ViewEncapsulation.None
})
export class GenerateTimesheetComponent implements OnDestroy{
  private subs = new SubSink();
  
  dateRanges: {id:string, title: string}[] = [
    {id: "today", title: "Today" },
    {id: "last-week", title: "Last Week" },
    {id: "last-pay-run", title: "Last Pay Run" }
  ];
  selectedDateRange: {id:string, title: string} = null;
  dateRange: IDateRange = {
    range: {
      from: this._util.getDateOnly(new Date()),
      to: this._util.getDateOnly(new Date())
    } 
  };
  yearRange = `2018:${addYears(new Date(), 5).getFullYear()}`;
  message = "";

  groups: IEntity[] = [];
  employees: EmployeeBase[] = [];
  jobCodes: IJobCode[] = [];
  employeesToShow: EmployeeBase[] = [];

  selectedGroups: IEntity[] = [];
  timesheetGenerationModel: TimesheetGenerationModel = {
    from: new Date(),
    to: new Date(),
    employees: [],
    hours: 12,
    dsHours: 11.5,
    nsHours: 12
  };

  maxToDate: Date = new Date();
  maxFromDate: Date = new Date();

  constructor(
    private _dialogRef: DynamicDialogRef,
    private _dlgConfig: DynamicDialogConfig,
    private _payrollService: PayrollService,
    private _timesheetService: TimesheetService,
    private _rosterCodeService: RosterCodeService,
    private _confirm: ConfirmationService,
    private _toast: ToastService,
    private _util: PpsUtilityService) {
    this.setData(this._dlgConfig.data);
  }

  ngOnDestroy(): void{
    this.subs.unsubscribe();    
  }

  onDateRangeChanged(event){
    this.setDateRange();
  }
  
  private setDateRange(){
    if (this.selectedDateRange){
      let range = this._util.getDateRange(this.selectedDateRange.id);
      if (range){
        this.timesheetGenerationModel.from = range.from;
        this.timesheetGenerationModel.to = range.to;
      }else{
        if (this.selectedDateRange.id === "last-pay-run"){
          var lastPayPeriod = this._payrollService.getLastPayPeriod();
          this.timesheetGenerationModel.from = lastPayPeriod.range.from;
          this.timesheetGenerationModel.to = lastPayPeriod.range.to;
        }
      }
    }
  }

  private setData(data: any){
    if (!data){
      return;
    }

    this.groups = data.groups;
    this.employees = data.employees;
    this.jobCodes = data.jobCodes;
  }

  updateEmployeeList(){
    // this.selectedGroups values from model binding
    var selectedGroupsDict = {};
    this.selectedGroups.forEach(g => selectedGroupsDict[g.id] = 1);
    
    var employees: EmployeeBase[] = [];

    this.timesheetGenerationModel.employees.forEach(e => {
      if (selectedGroupsDict.hasOwnProperty(e.workGroup.id)){
        employees.push(e);
      }
    });

    this.timesheetGenerationModel.employees = employees;

    this.employeesToShow = this.employees.filter(e => e.dateOfBirth && selectedGroupsDict.hasOwnProperty(e.workGroup.id));
  }

  cancel(){
    this._dialogRef.close();
  }

  generateTimesheet(form: NgForm){
    if (form.invalid){
      return false;
    }

    this.checkEmployeesPayrollRosterCode(this.timesheetGenerationModel, 
      () => {
        this.generateTimesheetHelper();
      }
    );
  }

  private generateTimesheetHelper(){
    if (this.timesheetGenerationModel.from > this.timesheetGenerationModel.to){
      this.message = "From date must be earlier than To date";
      return;
    }

    this._dialogRef.close(this.timesheetGenerationModel);
  }

  private checkEmployeesPayrollRosterCode(timesheetGenerationModel: TimesheetGenerationModel, fnContinue: Function){

    this.subs.sink = this._timesheetService.generateTimesheetPreview(timesheetGenerationModel)
      .subscribe(res=>{
        if (res.succeed ){
          let result: string[] = [];

          res.result.forEach(e => {
            let code = this._rosterCodeService.getRosterCode(e.payrollRosterCode.id);
            
            if (this._rosterCodeService.isOnSite(e.payrollRosterCode.id)){
              result.push(`${this._util.getDateString(e.day)}: <strong>${this._util.getEmployeeName(e.employee)}</strong> - ${code.code}`);
            }
          });

          if (result.length > 0){
            this._confirm.confirm({
              header: `Confirmation for Employees with Day/Night Shift`,
              message: `Are you sure ${result.length === 1 ? "this employee" : "the following employees"} attended work and ${result.length === 1 ? "was" : "were"} not sick? <br/> <br/>${result.join('<br>')}`,
              blockScroll: false,
              defaultFocus: "accept",
              accept: () => {
                fnContinue();
              }
            });                    
          }else{
            fnContinue();
          }
        }else{
          this._toast.failed(res.errors.join(";"), 10000, "gtf");
        }
      },
      err => {},
      () => {});
  }  
}